import React, { useEffect, useState } from "react";
import { Modal, Zoom, LinearProgress, Fade } from "@mui/material";
import { styled } from "@mui/material/styles";
import { advanceState } from "features/gamestate/GameState";
import { useDispatch, useSelector } from "react-redux";
import {
  selectGameId,
  selectGameObject,
  selectGameStatusObject,
} from "features/gamestate/gameStateSlice";
import {
  selectAllPlayers,
  selectPlayerInControl,
} from "features/player/playerSlice";
import {
  GAMESTATE_ROUND_FINAL,
  GAMESTATE_ROUNDSTATE_DAILYDOUBLE_PICKED,
  GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING,
  GAMESTATE_ROUNDSTATE_QUESTION_JUDGING,
  GAMESTATE_ROUNDSTATE_QUESTION_WAGERING,
} from "features/gamestate/constants";
import { DDResult, DDRevealBox, DDWager } from "./DailyDoubleGameBoardQuestion";
import {
  GameBoardClue,
  GameBoardQuestionCountdown,
  GameBoardQuestionHeader,
  GameBoardQuestionResults,
} from "./GameBoardQuestion";
import { selectCurrentQuestionObject } from "features/question/questionSlice";
import {
  selectActiveDDSubmission,
  stopWatchingSubmissionAction,
  watchSingleSubmissionAction,
} from "features/question/submissionSlice";

const StyledModal = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// Modal sits in upper-center of viewport so it never overlaps the
// ClosedCaptioning overlay (which is fixed bottom, max 30vh tall).
// Hard cap at 70vh; long clues shrink via clamp() in ClueText.
const ModalPaper = styled('div')(({ theme }) => ({
  border: 'none',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4, 5),
  fontFamily: "'Poppins', 'Inter', sans-serif",
  width: 'min(900px, 92vw)',
  minHeight: '420px',
  maxHeight: '65vh',
  color: '#ffffff',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  // Subtle radial vignette and teal-glow ring — more depth than a flat fill,
  // still reads as the Groupardy navy at a glance.
  background: 'radial-gradient(ellipse at center, #22223e 0%, #14142a 100%)',
  boxShadow: '0 12px 40px rgba(0,0,0,0.55), 0 0 0 2px rgba(46, 196, 182, 0.28), 0 0 80px rgba(46, 196, 182, 0.10)',
  overflow: 'hidden',
  // Move the modal up a bit so even with min-height it leaves room for
  // the captioning at the bottom.
  marginBottom: '12vh',
}));

const GameBoardModal = () => {
  const dispatch = useDispatch();

  const [advancingState, setAdvancingState] = useState(false);

  const gameId = useSelector(selectGameId);
  const gameObj = useSelector(selectGameObject);
  const players = useSelector(selectAllPlayers);
  const gameStateObj = useSelector(selectGameStatusObject);
  const playerInControl = useSelector(selectPlayerInControl);
  const questionObj = useSelector(selectCurrentQuestionObject);
  const ddSubmission = useSelector(selectActiveDDSubmission);

  const isFinal = gameStateObj.round === GAMESTATE_ROUND_FINAL;
  const isDD = gameStateObj.dailyDouble;

  useEffect(() => {
    if (isDD && gameId && playerInControl && questionObj) {
      // Watch the player submission if it's a daily double
      dispatch(
        watchSingleSubmissionAction(gameId, playerInControl.id, questionObj.id)
      );
    }
    return function cleanup() {
      if (gameId && questionObj && playerInControl) {
        dispatch(
          stopWatchingSubmissionAction(
            gameId,
            questionObj.id,
            playerInControl.id
          )
        );
      }
    };
  }, [isDD, dispatch, gameId, questionObj, playerInControl]);

  // Click-to-advance is a host UX shortcut for display-only phases. It must
  // NOT fire during ANSWERING (would cut submissions off mid-flight) or
  // JUDGING (would commit before autoApproval finishes, silently dropping
  // submissions with status='submitted'). The host uses the explicit buttons
  // in GameStateButtons for those states.
  const NO_ADVANCE_STATES = new Set([
    GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING,
    GAMESTATE_ROUNDSTATE_QUESTION_JUDGING,
  ]);

  const onClick = async () => {
    if (advancingState) {
      return;
    }
    if (NO_ADVANCE_STATES.has(gameStateObj.roundState)) {
      return;
    }
    setAdvancingState(true);
    await advanceState(gameObj, players, dispatch);
    setAdvancingState(false);
  };

  let questionBody = () => {
    if (!questionObj) {
      return <div />;
    }

    let pointsLabel = questionObj.label;
    let ddResult = null;
    if (isDD && ddSubmission) {
      pointsLabel = `$${ddSubmission.wager.toLocaleString()}`;
      ddResult = (
        <DDResult
          answer={questionObj.answers[0]}
          gameState={gameStateObj}
          guess={ddSubmission.answer}
          playerName={playerInControl ? playerInControl.name : null}
          wager={ddSubmission.wager}
          correct={ddSubmission.result === "correct"}

        />
      );
    }

    return (
      <React.Fragment>
        <div style={{ flexShrink: 0 }}>
          <GameBoardQuestionHeader
            category={questionObj.category}
            pointsLabel={pointsLabel}
            showPoints={!isFinal}
            isWager={isDD}
          />
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px 0',
          }}
        >
          {isFinal ? (
            <Fade in={true} timeout={1200}>
              <div>
                <GameBoardClue clueText={questionObj.clue} />
              </div>
            </Fade>
          ) : (
            <GameBoardClue clueText={questionObj.clue} />
          )}
        </div>
        <div style={{ flexShrink: 0 }}>
          <GameBoardQuestionCountdown
            gameState={gameStateObj}
            questionObject={questionObj}
            gameId={gameId}
          />
          {isDD ? (
            ddResult
          ) : (
            <GameBoardQuestionResults
              gameState={gameStateObj}
              answer={questionObj.answers[0]}
            />
          )}
        </div>
      </React.Fragment>
    );
  };

  let body;

  switch (gameStateObj.roundState) {
    case GAMESTATE_ROUNDSTATE_DAILYDOUBLE_PICKED:
      body = <DDRevealBox />;
      break;
    case GAMESTATE_ROUNDSTATE_QUESTION_WAGERING:
      let wager = null;
      if (ddSubmission) {
        wager = ddSubmission.wager;
      }
      body = (
        <DDWager
          playerName={playerInControl ? playerInControl.name : null}
          wager={wager}
        />
      );
      break;
    default:
      body = questionBody();
      break;
  }

  return (
    <StyledModal
      open={true}
      closeAfterTransition
      BackdropProps={{
        timeout: 500,
        sx: { backgroundColor: "rgba(0,0,0,0.65)" }
      }}
    >
      <Zoom in={true} timeout={250}>
        <ModalPaper onClick={onClick}>
          {advancingState ? <LinearProgress /> : ""}
          {body}
        </ModalPaper>
      </Zoom>
    </StyledModal>
  );
};

export { GameBoardModal };
